// Shared Talmia 2.0 React primitives — thin wrappers over the .t2-* CSS.
// Import from "./_t2" wherever you need Panel/Chip/etc.
import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";

/** Panel — the atomic container. Every group of fields lives inside one. */
export function T2Panel({ icon: Icon, title, right, children, testid, className = "", padded = true, style }) {
  return (
    <section className={`t2-panel ${className}`} data-testid={testid} style={style}>
      {title != null && (
        <header className="t2-panel-h">
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            {Icon && <Icon className="w-3.5 h-3.5" />}
            {title}
          </span>
          {right && <span className="t2-annot">{right}</span>}
        </header>
      )}
      {padded
        ? <div className="t2-panel-body">{children}</div>
        : children}
    </section>
  );
}

/** Tier chip — semantic colour by tier name (Talmia 2.0 brass palette). */
export function T2TierChip({ tier }) {
  if (!tier) return null;
  const s = String(tier).toLowerCase();
  let cls = "tier-silver";
  if (s.includes("platinum")) cls = "tier-platinum";
  else if (s.includes("gold")) cls = "tier-gold";
  else if (s.includes("bronze")) cls = "tier-bronze";
  else if (s.includes("black")) cls = "tier-black";
  return <span className={`t2-chip ${cls}`}>★ {tier.replace(/\s*tier$/i, "")}</span>;
}

/** Chip — generic tone chip. */
export function T2Chip({ tone = "", children, className = "" }) {
  return <span className={`t2-chip ${tone} ${className}`}>{children}</span>;
}

/** Stat box (mono value + upper-cased label). */
export function T2Stat({ label, value }) {
  return (
    <div className="t2-stat">
      <div className="t2-stat-val">{value}</div>
      <div className="t2-stat-lbl">{label}</div>
    </div>
  );
}

/** Simple label above input helper. */
export function T2Label({ children }) {
  return <div className="t2-label">{children}</div>;
}

/** Command palette overlay — used by QuoteBuilder product search.
 *  onSelect(item) fires with the chosen row. onClose called when user dismisses. */
export function T2CommandPalette({ open, onClose, onSelect, onQuery, items, placeholder, renderRow, renderActions, empty, emptyAction, toolbar }) {
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setQ(""); setActive(0);
    setTimeout(() => inputRef.current?.focus(), 40);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    onQuery?.(q);
    setActive(0);
     
  }, [q, open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
      if (e.key === "ArrowDown") { e.preventDefault(); setActive(a => Math.min(a + 1, items.length - 1)); return; }
      if (e.key === "ArrowUp")   { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); return; }
      if (e.key === "Enter" && items[active]) { e.preventDefault(); onSelect(items[active]); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, items, active, onClose, onSelect]);

  if (!open) return null;

  return createPortal(
    <div className="t2-cmd-overlay" onClick={onClose} data-testid="t2-cmd-overlay">
      <div className="t2-cmd" onClick={(e) => e.stopPropagation()}>
        <div className="t2-cmd-search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: "var(--t2-orange)" }}>
            <circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/>
          </svg>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={placeholder || "Search…"}
            data-testid="t2-cmd-input"
          />
          <kbd>ESC</kbd>
        </div>
        {toolbar && (
          <div
            className="t2-cmd-toolbar"
            style={{
              display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
              padding: "8px 12px", borderBottom: "1px solid rgba(255,255,255,0.06)",
            }}
          >
            {toolbar}
          </div>
        )}
        <div className="t2-cmd-list t2-scroll">
          {items.length === 0 && <div className="t2-cmd-empty">{empty || "No results."}</div>}
          {items.map((it, i) => (
            <div key={it._key || i}
                 className={`t2-cmd-row ${i === active ? "active" : ""} ${renderActions ? "t2-cmd-row--with-actions" : ""}`}
                 onClick={() => onSelect(it)}
                 onMouseEnter={() => setActive(i)}
                 data-testid={`t2-cmd-row-${i}`}>
              {renderRow(it)}
              {renderActions && (
                <div
                  className="t2-cmd-row-actions"
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`t2-cmd-row-actions-${i}`}
                >
                  {renderActions(it, { close: onClose })}
                </div>
              )}
            </div>
          ))}
          {emptyAction && items.length === 0 && (
            <div className="t2-cmd-empty-action" data-testid="t2-cmd-empty-action" style={{ padding: 12, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
              {typeof emptyAction === "function" ? emptyAction({ query: q, close: onClose }) : emptyAction}
            </div>
          )}
        </div>
        <div className="t2-cmd-foot">
          <span><kbd>↑↓</kbd> Navigate <kbd>↵</kbd> Select</span>
          <span>{items.length} match{items.length === 1 ? "" : "es"}</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
